import { Link } from "react-router-dom";
import SEOHead from "../../components/shared/SEOHead.jsx";
import SimplePage from "../../components/shared/SimplePage.jsx";

const KONTAKT_PATH = { de: "kontakt", en: "contact" };

const sectionTitleStyle = {
  fontFamily: "var(--font-serif)",
  fontStyle: "italic",
  fontSize: "15px",
  color: "var(--color-ink)",
  margin: "0 0 4px",
};

const subheadStyle = {
  fontFamily: "var(--font-serif)",
  fontSize: "19px",
  color: "var(--color-ink)",
  margin: "8px 0 0",
};

const bodyStyle = { fontSize: "16px", lineHeight: 1.7, color: "rgba(10,9,7,0.85)", margin: 0 };

function Section({ title, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      <p style={sectionTitleStyle}>{title}</p>
      <p style={bodyStyle}>{children}</p>
    </div>
  );
}

/**
 * Structured per konzepte/DE_VORLAGE_Impressum.pdf ("Deutsches Ehrenamt"
 * sample Impressum) — all facts are ours (see Gesellschaftsvertrag.jsx /
 * earlier Impressum content), only the sections the template requires and
 * we were missing got added: Kontakt, Wirtschafts-ID, the EU dispute-
 * resolution notice, and the full Disclaimer block. No Aufsichtsbehörde
 * section — the template marks it "(soweit vorhanden)" and none applies to
 * a LARP-event nonprofit. The Wirtschafts-ID (§ 139c AO) is a new,
 * gradually-rolled-out identifier from the Bundeszentralamt für Steuern —
 * left marked pending rather than invented, same treatment as the org's
 * still-open facts elsewhere (signatory, bank details).
 *
 * One deliberate deviation from the template: it also includes a link to
 * the EU's Online-Streitbeilegung (ODR) platform. That platform was
 * discontinued in 2025, so stating it's available would be actively
 * wrong — omitted here, keeping only the still-accurate non-participation
 * statement. Flag if this reasoning turns out to be outdated.
 *
 * German is the legally binding version (§ 5 DDG requires the German
 * text); the English block is a courtesy translation, same convention as
 * the other legal pages. AGB and Datenschutz remain separate, still-open
 * placeholders.
 */
export default function Impressum({ lang }) {
  const path = lang === "de" ? "/impressum" : "/legal-notice";
  const altPath = lang === "de" ? "/legal-notice" : "/impressum";
  const title = lang === "de" ? "Impressum" : "Legal Notice";

  return (
    <>
      <SEOHead lang={lang} path={path} altPath={altPath} title={title} noindex />
      <SimplePage title={title}>
        {lang === "de" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <p style={bodyStyle}>Angaben gemäß § 5 DDG</p>

            <Section title="Anschrift">
              Tales&amp;Legends gemeinnützige UG (haftungsbeschränkt)
              <br />
              Reiboldschachtring 28
              <br />
              01705 Freital
            </Section>

            <Section title="Vertretungsberechtigte Person">
              Geschäftsführer: Nils Jäkel
            </Section>

            <Section title="Kontakt">
              E-Mail: <a href="mailto:hello@talesandlegends.org">hello@talesandlegends.org</a>
              <br />
              Oder über unser <Link to={`/${lang}/${KONTAKT_PATH[lang]}`}>Kontaktformular</Link>.
            </Section>

            <Section title="Registereintrag">
              Registergericht: Amtsgericht Dresden
              <br />
              Registernummer: HRB 48120
            </Section>

            <Section title="Steuerliche Angaben">
              Steuernummer: 210/124/00575
              <br />
              Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: DE463154242
              <br />
              Wirtschafts-Identifikationsnummer gemäß § 139c AO: wird nach
              Zuteilung durch das Bundeszentralamt für Steuern ergänzt.
            </Section>

            <Section title="Gemeinnützigkeit">
              Bescheid über die gesonderte Feststellung der Einhaltung der
              satzungsmäßigen Voraussetzungen nach § 60a Abs. 1 AO vom 09. Juni
              2026, ausgestellt durch das Finanzamt Pirna (Steuernummer:
              210/124/00575).
              <br />
              <br />
              Geförderte gemeinnützige Zwecke:
              <br />
              — die Förderung von Kunst und Kultur
              <br />
              — die Förderung von Jugend- und Altenhilfe
              <br />
              — die Förderung des bürgerschaftlichen Engagements
            </Section>

            <Section title="Inhaltlich verantwortlich">
              Nils Jäkel
              <br />
              Reiboldschachtring 28
              <br />
              01705 Freital
              <br />
              gemäß § 18 Abs. 2 MStV
            </Section>

            <Section title="Streitbeilegung">
              Tales&amp;Legends gemeinnützige UG (haftungsbeschränkt) ist nicht
              bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor
              einer Verbraucherschlichtungsstelle teilzunehmen.
            </Section>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <p style={sectionTitleStyle}>Disclaimer</p>

              <p style={subheadStyle}>Inhalte unserer Website</p>
              <p style={bodyStyle}>
                Die Inhalte dieser Website wurden mit größter Sorgfalt erstellt.
                Als Inhaltsanbieter (sog. Content-Provider) sind wir für eigene
                Inhalte auf diesen Seiten nach den allgemeinen Gesetzen
                verantwortlich. Für die Qualität, Richtigkeit, Vollständigkeit
                und Aktualität der Inhalte übernehmen wir jedoch keine Gewähr.
                Haftungsansprüche gegen uns, welche sich auf Schäden
                materieller oder ideeller Art beziehen, die durch die Nutzung
                oder Nichtnutzung der dargebotenen Informationen bzw. durch
                die Nutzung fehlerhafter und unvollständiger Informationen
                verursacht wurden, sind grundsätzlich ausgeschlossen, sofern
                kein nachweislich vorsätzliches oder grob fahrlässiges
                Verschulden unsererseits vorliegt. Alle Angebote sind
                freibleibend und unverbindlich. Wir behalten es uns
                ausdrücklich vor, Teile der Seiten oder das gesamte Angebot
                ohne gesonderte Ankündigung zu verändern, zu ergänzen, zu
                löschen oder die Veröffentlichung zeitweise oder endgültig
                einzustellen.
              </p>

              <p style={subheadStyle}>Verfügbarkeit unserer Website</p>
              <p style={bodyStyle}>
                Wir werden uns bemühen, die Website möglichst
                unterbrechungsfrei zum Abruf anzubieten. Auch bei aller
                Sorgfalt können aber Ausfallzeiten nicht ausgeschlossen
                werden. Wir behalten uns das Recht vor, die Website oder Teile
                davon jederzeit zu ändern oder einzustellen. Für durch nicht
                fehlerfrei angelegte Dateien oder nicht fehlerfrei
                strukturierte Formate bedingte Unterbrechungen oder
                anderweitige Störungen übernehmen wir keine Gewähr.
              </p>

              <p style={subheadStyle}>Verweise und Links</p>
              <p style={bodyStyle}>
                Unser Angebot kann direkte und indirekte Verlinkungen
                („Hyperlink") zu externen Webseiten Dritter enthalten, auf
                deren Inhalte wir keinen Einfluss haben. Deshalb können wir
                für diese fremden Inhalte auch keine Gewähr übernehmen. Für
                die Inhalte der verlinkten Seiten sowie für illegale,
                fehlerhafte oder unvollständige Inhalte und insbesondere für
                Schäden, die aus der Nutzung oder Nichtnutzung solcherart
                dargebotener Informationen entstehen, haftet allein der
                Anbieter oder Betreiber der jeweiligen Webseite. Eine
                Haftungsverpflichtung unsererseits besteht ausschließlich in
                dem Fall, in dem wir von den Inhalten Kenntnis haben und es
                uns technisch möglich und zumutbar wäre, die Nutzung im Falle
                rechtswidriger Inhalte zu verhindern. Wir erklären hiermit
                ausdrücklich, dass zum Zeitpunkt der Linksetzung keine
                illegalen Inhalte auf den zu verlinkenden Seiten erkennbar
                waren. Auf die aktuelle und zukünftige Gestaltung, die
                Inhalte oder die Urheberschaft der verlinkten Seiten haben
                wir keinerlei Einfluss und überprüfen die Inhalte auch nicht
                ständig auf Veränderungen. Deshalb distanzieren wir uns
                hiermit ausdrücklich von allen Inhalten aller verlinkten
                Seiten, die nach der Linksetzung verändert wurden. Wenn wir
                feststellen oder von anderen darauf hingewiesen werden, dass
                ein konkreter Inhalt, auf den mittels Hyperlink verwiesen
                wird, eine zivil- oder strafrechtliche Verantwortung
                auslöst, werden wir den Verweis unverzüglich aufheben.
              </p>

              <p style={subheadStyle}>Urheberrecht</p>
              <p style={bodyStyle}>
                Diese Website befindet sich in unserem Eigentum. Inhaber
                sämtlicher gewerblicher Schutzrechte, insbesondere der Marken
                sowie sämtlicher sonstiger Schutzrechte, insbesondere der
                Urheberrechte/urheberrechtlichen Nutzungsrechte an den auf
                dieser Website bereitgestellten Inhalten, sind wir. Der
                Inhalt dieser Website darf insbesondere nicht kopiert,
                reproduziert oder in anderer Weise vervielfältigt werden. Die
                Wiederveröffentlichung, Bearbeitung, Sendung oder Verbreitung
                in dieser oder sonstiger Weise, insbesondere die Nutzung auf
                anderen Websites oder Netzwerkumgebungen, ist ohne vorherige
                schriftliche Einwilligung durch uns unzulässig. Die
                Veröffentlichung im World Wide Web oder in sonstigen Diensten
                des Internets bedeutet noch keine Einverständniserklärung für
                eine entsprechende Nutzung durch Dritte.
              </p>
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            <p style={bodyStyle}>
              Information according to § 5 DDG (German Digital Services Act,
              which replaced the former TMG). The German version above is
              the legally binding one; this is a courtesy translation.
            </p>

            <Section title="Address">
              Tales&amp;Legends gemeinnützige UG (haftungsbeschränkt)
              <br />
              Reiboldschachtring 28
              <br />
              01705 Freital, Germany
            </Section>

            <Section title="Authorized representative">
              Managing director: Nils Jäkel
            </Section>

            <Section title="Contact">
              Email: <a href="mailto:hello@talesandlegends.org">hello@talesandlegends.org</a>
              <br />
              Or via our <Link to={`/${lang}/${KONTAKT_PATH[lang]}`}>contact form</Link>.
            </Section>

            <Section title="Register entry">
              Register court: Amtsgericht Dresden
              <br />
              Register number: HRB 48120
            </Section>

            <Section title="Tax details">
              Tax number: 210/124/00575
              <br />
              VAT identification number per § 27a UStG: DE463154242
              <br />
              Wirtschafts-Identifikationsnummer (business ID) per § 139c AO:
              to be added once assigned by the Federal Central Tax Office.
            </Section>

            <Section title="Non-profit status">
              Notice on the separate determination of compliance with the
              statutory requirements under § 60a (1) of the German Fiscal Code
              (AO), dated 9 June 2026, issued by the Pirna tax office
              (Finanzamt Pirna, tax number: 210/124/00575).
              <br />
              <br />
              Recognized charitable purposes:
              <br />
              — the promotion of art and culture
              <br />
              — the promotion of youth and elderly welfare
              <br />
              — the promotion of civic engagement
            </Section>

            <Section title="Responsible for content">
              Nils Jäkel
              <br />
              Reiboldschachtring 28
              <br />
              01705 Freital, Germany
              <br />
              per § 18 (2) MStV (German Interstate Media Treaty)
            </Section>

            <Section title="Dispute resolution">
              Tales&amp;Legends gemeinnützige UG (haftungsbeschränkt) is
              neither willing nor obligated to participate in dispute
              resolution proceedings before a consumer arbitration board.
            </Section>

            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <p style={sectionTitleStyle}>Disclaimer</p>

              <p style={subheadStyle}>Content of this website</p>
              <p style={bodyStyle}>
                This website's content was created with the greatest care. As
                a content provider, we are responsible for our own content on
                these pages under general law. However, we do not guarantee
                the quality, accuracy, completeness, or timeliness of the
                content. Liability claims against us relating to material or
                immaterial damage caused by the use or non-use of the
                information provided, or by the use of incorrect or
                incomplete information, are excluded in principle, provided
                there is no demonstrably intentional or grossly negligent
                fault on our part. All offers are non-binding. We expressly
                reserve the right to change, add to, delete, or temporarily
                or permanently discontinue publication of parts of the pages
                or the entire offering without separate notice.
              </p>

              <p style={subheadStyle}>Availability of this website</p>
              <p style={bodyStyle}>
                We will make an effort to offer the website for access as
                uninterruptedly as possible. However, despite all care,
                downtimes cannot be ruled out. We reserve the right to change
                or discontinue the website or parts of it at any time. We
                accept no liability for interruptions or other disruptions
                caused by improperly created files or improperly structured
                formats.
              </p>

              <p style={subheadStyle}>References and links</p>
              <p style={bodyStyle}>
                Our site may contain direct and indirect links ("hyperlinks")
                to external third-party websites over whose content we have
                no influence. We therefore cannot accept any responsibility
                for this third-party content. The provider or operator of the
                respective linked website is solely liable for its content
                and for any illegal, incorrect, or incomplete content, and in
                particular for any damage arising from the use or non-use of
                such information. We are only liable if we are aware of the
                content and it would be technically possible and reasonable
                for us to prevent its use in the case of unlawful content. We
                expressly declare that at the time the links were created, no
                illegal content was recognizable on the linked pages. We have
                no influence on the current or future design, content, or
                authorship of linked pages and do not continuously monitor
                them for changes. We therefore expressly distance ourselves
                from all content of any linked pages that was changed after
                the link was created. If we become aware that a specific
                linked page gives rise to civil or criminal liability, we
                will remove the link to it without delay.
              </p>

              <p style={subheadStyle}>Copyright</p>
              <p style={bodyStyle}>
                This website is our property. We hold all industrial property
                rights, in particular trademarks, and all other rights, in
                particular copyrights/usage rights, to the content provided
                on this website. The content of this website may not be
                copied, reproduced, or otherwise duplicated. Republication,
                editing, broadcasting, or distribution in this or any other
                way, in particular use on other websites or network
                environments, is not permitted without our prior written
                consent. Publication on the World Wide Web or other internet
                services does not constitute consent to corresponding use by
                third parties.
              </p>
            </div>
          </div>
        )}
      </SimplePage>
    </>
  );
}
